import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { can } from "@/lib/roles";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

export const TZ_OPTIONS = [
  { v: "America/New_York", l: "Eastern (New York)" },
  { v: "America/Chicago", l: "Central (Chicago)" },
  { v: "America/Denver", l: "Mountain (Denver)" },
  { v: "America/Phoenix", l: "Arizona (no DST)" },
  { v: "America/Los_Angeles", l: "Pacific (Los Angeles)" },
  { v: "America/Anchorage", l: "Alaska (Anchorage)" },
  { v: "Pacific/Honolulu", l: "Hawaii (Honolulu)" },
];
import { Plus, Building2, Loader2, MapPin, Trash2, Archive, ArchiveRestore } from "lucide-react";
import { PROPERTY_COLORS, propColor } from "@/lib/propertyColors";

const EMPTY = { name: "", dba_name: "", llc_name: "", address: "", city: "", state: "", brand: "", rooms: 0, room_numbers: "", timezone: "America/New_York", color: "#3b82f6" };

export default function Properties() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [props, setProps] = useState(null);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  const editable = can(user, ["admin", "regional"]);
  const canDelete = can(user, ["admin"]);
  const [deletingId, setDeletingId] = useState(null);

  const load = () => api.get("/properties").then((r) => setProps(r.data)).catch(() => setProps([]));
  useEffect(() => {
    load();
  }, []);

  const daysLeft = (p) => (p.archived_at ? Math.max(0, 30 - Math.floor((Date.now() - new Date(p.archived_at).getTime()) / 86400000)) : 30);

  const archive = async (e, p, archived) => {
    e.stopPropagation();
    if (archived && !window.confirm(`Archive "${p.name}"? It stays visible (marked Archived) and can be restored any time. Permanent delete unlocks after 30 days.`)) return;
    setDeletingId(p.id);
    try {
      await api.post(`/properties/${p.id}/archive`, { archived });
      toast.success(archived ? "Property archived" : "Property restored");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setDeletingId(null);
    }
  };

  const remove = async (e, p) => {
    e.stopPropagation();
    if (!window.confirm(`Permanently delete "${p.name}"? This removes the property and ALL its reports, payroll, invoices, documents and employees. This cannot be undone.`)) return;
    if (window.prompt(`Type the property name to confirm: ${p.name}`) !== p.name) return toast.error("Name didn't match — not deleted");
    setDeletingId(p.id);
    try {
      await api.delete(`/properties/${p.id}`);
      toast.success("Property deleted");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setDeletingId(null);
    }
  };

  const setF = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      await api.post("/properties", { ...form, rooms: Number(form.rooms) || 0 });
      toast.success("Property created");
      setOpen(false);
      setForm(EMPTY);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  if (props === null) {
    return (
      <div className="flex h-64 items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-8" data-testid="properties-page">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="label-caps text-primary">Portfolio</div>
          <h1 className="font-heading text-3xl font-black tracking-tight lg:text-4xl">Properties</h1>
          <p className="mt-1 text-sm text-muted-foreground">{props.length} propert{props.length === 1 ? "y" : "ies"} under management.</p>
        </div>
        {editable && (
          <Button onClick={() => setOpen(true)} data-testid="add-property-btn">
            <Plus className="mr-2 h-4 w-4" /> New Property
          </Button>
        )}
      </div>

      <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
        {props.map((p) => {
          const c = propColor(p);
          return (
          <Card
            key={p.id}
            onClick={() => navigate(`/properties/${p.id}`)}
            className={`group relative cursor-pointer overflow-hidden rounded-none border p-6 transition-transform hover:-translate-y-1 ${p.archived ? "opacity-60 grayscale" : ""}`}
            data-testid={`property-card-${p.id}`}
          >
            <div className="absolute inset-x-0 top-0 h-1" style={{ backgroundColor: c }} data-testid={`property-color-bar-${p.id}`} />
            <div className="flex items-start justify-between">
              <div className="flex h-10 w-10 items-center justify-center text-white" style={{ backgroundColor: c }}>
                <Building2 className="h-5 w-5" />
              </div>
              <div className="flex items-center gap-2">
                <span className="label-caps text-muted-foreground">{p.rooms || 0} rooms</span>
                {canDelete && !p.archived && (
                  <button
                    onClick={(e) => archive(e, p, true)}
                    disabled={deletingId === p.id}
                    className="p-1 text-muted-foreground opacity-0 transition-opacity hover:text-amber-600 group-hover:opacity-100"
                    title="Archive property"
                    data-testid={`archive-property-${p.id}`}
                  >
                    {deletingId === p.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <Archive className="h-4 w-4" />}
                  </button>
                )}
              </div>
            </div>
            {p.archived && (
              <div className="mt-3 flex flex-wrap items-center gap-2" data-testid={`archived-tag-${p.id}`}>
                <span className="bg-amber-500/15 px-2 py-0.5 text-xs font-bold uppercase tracking-wide text-amber-700 dark:text-amber-400">Archived</span>
                <span className="text-xs text-muted-foreground">{p.archived_by ? `by ${p.archived_by} · ` : ""}{p.archived_at ? new Date(p.archived_at).toLocaleDateString() : ""}</span>
                {canDelete && (
                  <>
                    <Button size="sm" variant="outline" className="h-7" onClick={(e) => archive(e, p, false)} data-testid={`restore-property-${p.id}`}><ArchiveRestore className="mr-1 h-3.5 w-3.5" /> Restore</Button>
                    <Button size="sm" variant="outline" className="h-7 border-red-300 text-red-700" disabled={daysLeft(p) > 0 || deletingId === p.id}
                      onClick={(e) => remove(e, p)} title={daysLeft(p) > 0 ? `Delete unlocks in ${daysLeft(p)} day(s)` : "Permanently delete"} data-testid={`delete-property-${p.id}`}>
                      <Trash2 className="mr-1 h-3.5 w-3.5" /> {daysLeft(p) > 0 ? `Delete in ${daysLeft(p)}d` : "Delete"}
                    </Button>
                  </>
                )}
              </div>
            )}
            <h3 className="mt-4 font-heading text-xl font-bold tracking-tight">{p.name}</h3>
            {p.brand && <div className="text-sm text-muted-foreground">{p.brand}</div>}
            <div className="mt-3 flex items-center gap-1.5 text-sm text-muted-foreground">
              <MapPin className="h-3.5 w-3.5" />
              {[p.city, p.state].filter(Boolean).join(", ") || "No location"}
            </div>
            {p.llc_name && (
              <div className="mt-4 border-t border-border pt-3 text-xs text-muted-foreground">
                <span className="label-caps">LLC</span> · {p.llc_name}
              </div>
            )}
          </Card>
          );
        })}
        {props.length === 0 && (
          <Card className="col-span-full rounded-none border border-dashed p-12 text-center">
            <Building2 className="mx-auto h-8 w-8 text-muted-foreground" />
            <p className="mt-3 text-sm text-muted-foreground">No properties yet. Add your first property to get started.</p>
          </Card>
        )}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg" data-testid="property-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">New Property</DialogTitle>
            <DialogDescription>Add a hotel property with its LLC, DBA and location details.</DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-4 py-2">
            <div className="col-span-2 space-y-1.5">
              <Label>Property Name *</Label>
              <Input value={form.name} onChange={(e) => setF("name", e.target.value)} data-testid="prop-name" />
            </div>
            <div className="space-y-1.5">
              <Label>DBA Name</Label>
              <Input value={form.dba_name} onChange={(e) => setF("dba_name", e.target.value)} />
            </div>
            <div className="space-y-1.5">
              <Label>LLC Name</Label>
              <Input value={form.llc_name} onChange={(e) => setF("llc_name", e.target.value)} />
            </div>
            <div className="space-y-1.5">
              <Label>Brand / Flag</Label>
              <Input value={form.brand} onChange={(e) => setF("brand", e.target.value)} />
            </div>
            <div className="space-y-1.5">
              <Label>Rooms</Label>
              <Input type="number" value={form.rooms} onChange={(e) => setF("rooms", e.target.value)} />
            </div>
            <div className="col-span-2 space-y-1.5">
              <Label>Address</Label>
              <Input value={form.address} onChange={(e) => setF("address", e.target.value)} />
            </div>
            <div className="space-y-1.5">
              <Label>City</Label>
              <Input value={form.city} onChange={(e) => setF("city", e.target.value)} />
            </div>
            <div className="space-y-1.5">
              <Label>State</Label>
              <Input value={form.state} onChange={(e) => setF("state", e.target.value)} />
            </div>
            <div className="space-y-1.5">
              <Label>Time Zone</Label>
              <Select value={form.timezone} onValueChange={(v) => setF("timezone", v)}>
                <SelectTrigger data-testid="prop-timezone"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {TZ_OPTIONS.map((t) => <SelectItem key={t.v} value={t.v}>{t.l}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="col-span-2 space-y-1.5">
              <Label>Room Numbers</Label>
              <Textarea
                value={form.room_numbers}
                onChange={(e) => setF("room_numbers", e.target.value)}
                placeholder="e.g. 101-120, 201-220, 305"
                data-testid="prop-room-numbers"
              />
              <p className="text-xs text-muted-foreground">Used by Housekeeping. Enter lists and ranges (e.g. 101-120, 201-220). You can edit these later on the property page.</p>
            </div>
            <div className="col-span-2 space-y-1.5">
              <Label>Accent Color</Label>
              <div className="flex flex-wrap items-center gap-2" data-testid="prop-color-picker">
                {PROPERTY_COLORS.map((c) => (
                  <button
                    key={c.v}
                    type="button"
                    onClick={() => setF("color", c.v)}
                    title={c.l}
                    data-testid={`prop-color-${c.v.replace("#", "")}`}
                    className={`h-7 w-7 rounded-full transition-transform hover:scale-110 ${form.color === c.v ? "ring-2 ring-offset-2 ring-offset-background" : ""}`}
                    style={{ backgroundColor: c.v, boxShadow: form.color === c.v ? `0 0 0 2px ${c.v}` : undefined }}
                  />
                ))}
              </div>
              <p className="text-xs text-muted-foreground">Shown on this property's card, header and badges so it's easy to spot.</p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={save} disabled={saving || !form.name} data-testid="save-property-btn">
              {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Create
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
